import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useI18n} from 'i18n/I18nContext'
import {LocalLink} from 'i18n/LocalLink'
import styled from 'styled-components'
import LaunchIcon from '@mui/icons-material/Launch'

import {
  useOrganizationEventRoutes,
  useOrganizationRoutes,
} from 'organization/OrganizationRoutes'
import {useOrganizationEvent} from 'organization/Event/OrganizationEventProvider'
import {useMeeting} from 'organization/Event/MeetingProvider'
import {getEventImageUrl} from 'event/models/Event'
import {useOrganization} from 'organization/providers/OrganizationProvider'

export const FirstSteps = () => {
  const {t} = useI18n()
  const [open, setOpen] = useState(false)
  const [stepsDone, setStepsDone] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const {organization} = useOrganization()

  const {event} = useOrganizationEvent()
  const {meeting} = useMeeting()
  const routes = useOrganizationEventRoutes()
  const organizationRoutes = useOrganizationRoutes()
  const step1Done = !!event?.paid
  const step2Done =
    !!event?.name &&
    !!event?.slug &&
    !!event?.date &&
    !!event?.page_date &&
    !!event?.time
  const step3Done = event?.price !== null
  const step4Done = organization?.is_stripe_connected ?? false
  const step5Done =
    !!event?.description &&
    !!event?.short_description &&
    !!getEventImageUrl(event, 1)
  const step6Done = !!event?.template
  const step7Done = !!event?.published_at
  const step8Done = !!meeting?.host_first_name && !!meeting?.host_last_name

  const steps = useMemo(
    () => [
      {text: t('firstSteps.s1'), done: step1Done, link: routes.settings},
      {
        text: t('firstSteps.s2'),
        done: step2Done,
        link: routes.settings,
      },
      {text: t('firstSteps.s3'), done: step3Done, link: routes.tickets},
      {
        text: t('firstSteps.s4'),
        done: step4Done,
        link: organizationRoutes.settings.root,
      },
      {
        text: t('firstSteps.s5'),
        done: step5Done,
        link: routes.publicPage,
      },
      {text: t('firstSteps.s6'), done: step6Done, link: routes.page},
      {text: t('firstSteps.s7'), done: step7Done, link: routes.settings},
      {
        text: t('firstSteps.s8'),
        done: step8Done,
        link: routes.management,
      },
    ],
    [
      t,
      routes,
      organizationRoutes,
      step1Done,
      step2Done,
      step3Done,
      step4Done,
      step5Done,
      step6Done,
      step7Done,
      step8Done,
    ],
  )

  useEffect(() => {
    setStepsDone(steps.filter((step) => step.done).length)
  }, [event, meeting, steps])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleOpen = () => setOpen((prev) => !prev)

  return (
    <Wrapper ref={containerRef}>
      <StyledButton
        type="button"
        onClick={toggleOpen}
        isDone={stepsDone === steps.length}
      >
        {t('firstSteps.title')}{' '}
        <i>
          ({stepsDone} / {steps.length})
        </i>
      </StyledButton>

      {open && (
        <PopupHolder>
          <Steps steps={steps} />
        </PopupHolder>
      )}
    </Wrapper>
  )
}

const Steps = ({
  steps,
}: {
  steps: {text: string; done: boolean; link: string}[]
}) => (
  <StepsHolder>
    {steps.map((step, idx) => (
      <Step key={idx} id={idx + 1} {...step} />
    ))}
  </StepsHolder>
)

const Step = ({
  id,
  text,
  done,
  link,
}: {
  id: number
  text: string
  done: boolean
  link: string
}) => (
  <StepHolder done={done}>
    <StepLeft done={done}>
      <StepId>{id}</StepId>
      <StepText>{text}</StepText>
    </StepLeft>
    <StepLink>
      <LocalLink to={link}>
        <LaunchIcon />
      </LocalLink>
    </StepLink>
  </StepHolder>
)

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`

export const StyledButton = styled.button<{
  isDone: boolean
}>`
  color: #fff;
  padding: 8px 18px;
  font-size: 14px;
  background: #0d1c42;
  border-radius: 25px;
  border: none;
  cursor: pointer;
  transition:
    box-shadow 0.3s,
    transform 0.3s;

  ${({isDone}) =>
    !isDone &&
    `
      box-shadow: 0 0 8px rgba(255, 0, 0, 0.6);
      animation: bounceGlow 1.3s ease-in-out infinite;
    `}

  @keyframes bounceGlow {
    0% {
      box-shadow: 0 0 8px rgba(255, 0, 0, 0.6);
    }
    50% {
      box-shadow: 0 0 16px rgba(255, 0, 0, 1);
    }
    100% {
      box-shadow: 0 0 8px rgba(255, 0, 0, 0.6);
    }
  }
`

const PopupHolder = styled.div`
  position: absolute;
  top: 65px;
  left: 0;
  background: #fff;
  padding: 18px;
  width: 500px;
  max-width: 90vw;
  max-height: calc(80vh - 180px);
  overflow: auto;
  border-radius: 6px;
  z-index: 1000;
  border: 3px solid transparent;
  background-image: linear-gradient(white, white), var(--clr-gradient-14);
  background-clip: padding-box, border-box;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    left: -56px;
  }
`
const StepsHolder = styled.div``
const StepHolder = styled.div<{done?: boolean}>`
  display: flex;
  justify-content: space-between;
  padding: 10px 10px;
  align-items: center;
  border-bottom: 1px dashed;

  background: ${({done}) => (done ? '#beffbe' : 'initial')};
`
const StepLeft = styled.div<{done?: boolean}>`
  display: flex;
  gap: 15px;
  text-decoration: ${({done}) => (done ? 'line-through' : 'none')};
`
const StepId = styled.div`
  background: #0d1c42;
  color: #ffffff;
  padding: 5px;
  border-radius: 100%;
  width: 25px;
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
`
const StepText = styled.div``
const StepLink = styled.div``
